import { useIntl } from 'react-intl';
import { Star } from 'lucide-react';
import { Input, Label } from 'reactstrap';

import CustomInput from 'src/componentes/CustomInput';

import ConfigApp from 'src/services/configApp';
import css from 'src/utils/css';
import FormularioGlobalMatrixInput from './FormularioGlobalMatrixInput';
import {
  deserializeMatrixAnswer,
  getMatrixAnswerValidationError,
  serializeMatrixAnswer,
} from './formularioGlobalMatrixAnswers';
import {
  FORMULARIO_GLOBAL_QUESTION_TYPES,
  getQuestionOptionsOrDefault,
  normalizeFormularioGlobalQuestionType,
  normalizeQuestionMatrixSettings,
} from './FormularioGlobalQuestionTypes';

import { getFormularioGlobalBranchingSections } from './formularioGlobalBranching';

const CLIENT_ID_FIELD = '_formularioGlobalClientId';

const classes = css`
  .page {
    background: #f5f7fa;
    min-height: 100vh;
    padding: 32px 16px;
  }

  .embedded {
    min-height: auto;
    padding: 24px 16px;
  }

  .container {
    margin: 0 auto;
    max-width: 860px;
  }

  .header {
    background: #ffffff;
    border: 1px solid #dde6e9;
    border-radius: 8px;
    margin-bottom: 16px;
    padding: 24px;
  }

  .title {
    color: #1f2d3d;
    font-size: 24px;
    font-weight: 700;
    line-height: 32px;
    margin: 0;
  }

  .description {
    color: #64748b;
    font-size: 14px;
    line-height: 22px;
    margin: 8px 0 0;
    white-space: pre-wrap;
  }

  .section {
    background: #ffffff;
    border: 1px solid #dde6e9;
    border-radius: 8px;
    margin-bottom: 16px;
    padding: 20px 24px;
  }

  .sectionTitle {
    color: #1f2d3d;
    font-size: 18px;
    font-weight: 700;
    line-height: 26px;
    margin: 0 0 4px;
  }

  .sectionDescription {
    color: #64748b;
    font-size: 14px;
    line-height: 22px;
    margin: 0 0 16px;
    white-space: pre-wrap;
  }

  .question {
    border-top: 1px solid #eef2f7;
    padding: 18px 0;
  }

  .question:first-child {
    border-top: 0;
    padding-top: 0;
  }

  .questionTitle {
    color: #1f2d3d;
    font-size: 15px;
    font-weight: 700;
    line-height: 22px;
    margin-bottom: 10px;
  }

  .required {
    color: #d9534f;
    margin-left: 3px;
  }

  .options {
    display: grid;
    gap: 8px;
  }

  .inlineOptions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .scaleOption {
    align-items: center;
    background: #ffffff;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    color: #1f2d3d;
    display: inline-flex;
    font-weight: 700;
    height: 38px;
    justify-content: center;
    min-width: 38px;
    padding: 0 10px;
  }

  .scaleOptionSelected {
    background: #0f7180;
    border-color: #0f7180;
    color: #ffffff;
  }

  .starRating {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }

  .starButton {
    align-items: center;
    background: transparent;
    border: 0;
    border-radius: 6px;
    color: #cbd5e1;
    display: inline-flex;
    height: 36px;
    justify-content: center;
    padding: 0;
    width: 36px;
  }

  .starButton:hover:not(:disabled) {
    background: #f8fafc;
    color: #d99a13;
  }

  .starButton:disabled {
    cursor: not-allowed;
  }

  .starButtonSelected {
    color: #d99a13;
  }

  .imageFrame {
    margin: 12px 0;
    max-width: 100%;
    overflow: hidden;
  }

  .imageFrame img {
    border-radius: 6px;
    display: block;
    height: auto;
    max-width: 100%;
    object-fit: contain;
  }

  .error {
    color: #d9534f;
    font-size: 12px;
    margin-top: 6px;
  }

  .actions {
    display: flex;
    justify-content: flex-end;
  }

  .finished {
    background: #ffffff;
    border: 1px solid #dde6e9;
    border-radius: 8px;
    padding: 28px;
    text-align: center;
  }

  .finishedActions {
    display: flex;
    justify-content: center;
    margin-top: 18px;
  }

  .singleQuestionContainer {
    max-width: 868px;
  }

  .singleQuestionHeader {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    margin-bottom: 0;
    padding: 12px 22px;
  }

  .singleQuestionHeaderStandalone {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
    margin-bottom: 20px;
  }

  .singleQuestionTitle {
    color: #64748b;
    font-size: 13px;
    font-weight: 500;
    line-height: 20px;
  }

  .singleQuestionDescription {
    font-size: 13px;
    line-height: 20px;
    margin-top: 4px;
  }

  .singleQuestionSection {
    border-top: 0;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    margin-bottom: 20px;
    padding: 26px 22px;
  }

  .singleQuestionSectionTitle {
    font-size: 15px;
    line-height: 22px;
  }

  .singleQuestionSectionDescription {
    margin-bottom: 18px;
  }

  .singleQuestionQuestion {
    border-top: 0;
    padding: 0;
  }

  .singleQuestionActions {
    display: block;
  }
`;

const getImageGuid = (imagem) => {
  if (!imagem) return null;
  if (typeof imagem === 'string') return imagem;

  return imagem.guid || imagem.imagemGuid || null;
};

const getImageUrl = (item) => {
  const imageGuid = getImageGuid(item?.imagem);

  if (!imageGuid) return null;

  return `${ConfigApp.ApiUrl}/ArquivoSistema/DownloadImagem?guid=${imageGuid}`;
};

const getSectionPreviewKey = (section = {}, index) =>
  section.id > 0
    ? `section-${section.id}`
    : `section-${section[CLIENT_ID_FIELD] || section.ordem || index}`;

const getQuestionPreviewKey = (question = {}, sectionKey, index) =>
  question.id > 0
    ? String(question.id)
    : `${sectionKey}-question-${
        question[CLIENT_ID_FIELD] || question.ordem || index
      }`;

const normalizePreviewMatrixItems = (items = []) =>
  [...items]
    .sort((a, b) => (a.ordem || 0) - (b.ordem || 0))
    .map((item, index) => ({
      ...item,
      [CLIENT_ID_FIELD]:
        item[CLIENT_ID_FIELD] || `preview-${item.ordem || index + 1}`,
    }));

const normalizeQuestion = (question = {}, intl, sectionKey, index) => {
  const tipo = normalizeFormularioGlobalQuestionType(
    question.tipo || question.tipoPergunta
  );

  return normalizeQuestionMatrixSettings({
    ...question,
    _formularioGlobalPreviewKey: getQuestionPreviewKey(
      question,
      sectionKey,
      index
    ),
    tipo,
    opcoes: getQuestionOptionsOrDefault(tipo, question.opcoes || [], intl),
    ...(tipo === FORMULARIO_GLOBAL_QUESTION_TYPES.MATRIZ_SELECAO
      ? {
          linhas: normalizePreviewMatrixItems(question.linhas),
          colunas: normalizePreviewMatrixItems(question.colunas),
        }
      : {}),
  });
};

const ROOT_ENTRY_TYPE = {
  SECTION: 'section',
  QUESTION: 'question',
};

const ROOT_QUESTIONS_GROUP_KEY_PREFIX = 'root';

const getOrderedRootEntries = (secoes = [], perguntas = []) =>
  [
    ...secoes.map((secao, sectionIndex) => ({
      type: ROOT_ENTRY_TYPE.SECTION,
      ordem: secao.ordem || 0,
      secao,
      sectionIndex,
    })),
    ...perguntas.map((pergunta) => ({
      type: ROOT_ENTRY_TYPE.QUESTION,
      ordem: pergunta.ordem || 0,
      pergunta,
    })),
  ].sort((a, b) => a.ordem - b.ordem);

export const buildFormularioGlobalSections = (formulario = {}, intl) => {
  const orderedRootEntries = getOrderedRootEntries(
    formulario.secoes,
    formulario.perguntas
  );

  const renderedSections = [];
  let bufferedRootQuestions = [];
  let rootQuestionsGroupIndex = 0;

  const flushBufferedRootQuestions = () => {
    if (bufferedRootQuestions.length === 0) return;

    const groupKey = `${ROOT_QUESTIONS_GROUP_KEY_PREFIX}-${rootQuestionsGroupIndex}`;
    rootQuestionsGroupIndex += 1;

    renderedSections.push({
      id: `perguntas-${groupKey}`,
      _formularioGlobalPreviewKey: groupKey,
      titulo: '',
      descricao: '',
      perguntas: bufferedRootQuestions.map((pergunta, questionIndex) =>
        normalizeQuestion(pergunta, intl, groupKey, questionIndex)
      ),
    });

    bufferedRootQuestions = [];
  };

  orderedRootEntries.forEach((rootEntry) => {
    if (rootEntry.type === ROOT_ENTRY_TYPE.QUESTION) {
      bufferedRootQuestions.push(rootEntry.pergunta);
      return;
    }

    flushBufferedRootQuestions();

    const sectionKey = getSectionPreviewKey(
      rootEntry.secao,
      rootEntry.sectionIndex
    );

    renderedSections.push({
      ...rootEntry.secao,
      _formularioGlobalPreviewKey: sectionKey,
      perguntas: [...(rootEntry.secao.perguntas || [])]
        .sort((a, b) => (a.ordem || 0) - (b.ordem || 0))
        .map((question, questionIndex) =>
          normalizeQuestion(question, intl, sectionKey, questionIndex)
        ),
    });
  });

  flushBufferedRootQuestions();

  return renderedSections;
};

const getQuestionValue = (answers, question) =>
  answers[question._formularioGlobalPreviewKey]?.valor;

const getQuestionComment = (answers, question) =>
  answers[question._formularioGlobalPreviewKey]?.comentario || '';

export const hasFormularioGlobalValue = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.trim().length > 0;

  return value !== null && value !== undefined && value !== '';
};

export const getFormularioGlobalAnswerValidationError = (
  question,
  answer = {}
) => {
  const matrixError = getMatrixAnswerValidationError(question, answer.valor);
  if (matrixError) return matrixError;

  if (question.obrigatoria && !hasFormularioGlobalValue(answer.valor)) {
    return 'campoObrigatorioNaoInformado';
  }

  if (
    question.permitirComentario &&
    question.comentarioObrigatorio &&
    !hasFormularioGlobalValue(answer.comentario)
  ) {
    return 'campoObrigatorioNaoInformado';
  }

  return null;
};

const findQuestionOptionByValue = (question, value) =>
  (question.opcoes || []).find(
    (opcao) => String(opcao.valor ?? opcao.id) === String(value)
  );

const toNumberOrNull = (value) => {
  if (!hasFormularioGlobalValue(value)) return null;

  const numberValue = Number(String(value).replace(',', '.'));

  return Number.isNaN(numberValue) ? null : numberValue;
};

const toBooleanOrNull = (value) => {
  if (!hasFormularioGlobalValue(value)) return null;

  if (value === true || value === 'true' || value === '1' || value === 1) {
    return true;
  }

  if (value === false || value === 'false' || value === '0' || value === 0) {
    return false;
  }

  return null;
};

const serializeQuestionAnswer = (question, answer = {}) => {
  const baseAnswer = {
    perguntaId: question.id,
    comentario: answer.comentario || null,
  };

  switch (question.tipo) {
    case FORMULARIO_GLOBAL_QUESTION_TYPES.MATRIZ_SELECAO:
      return {
        ...baseAnswer,
        selecoesMatriz: serializeMatrixAnswer(question, answer.valor),
      };
    case FORMULARIO_GLOBAL_QUESTION_TYPES.RESPOSTA_CURTA:
    case FORMULARIO_GLOBAL_QUESTION_TYPES.TEXTO_PARAGRAFO:
      return {
        ...baseAnswer,
        valorTexto: answer.valor || null,
      };
    case FORMULARIO_GLOBAL_QUESTION_TYPES.NPS:
      return {
        ...baseAnswer,
        valorNumero: toNumberOrNull(answer.valor),
      };
    case FORMULARIO_GLOBAL_QUESTION_TYPES.SIMNAO:
      return {
        ...baseAnswer,
        valorBooleano: toBooleanOrNull(answer.valor),
      };
    case FORMULARIO_GLOBAL_QUESTION_TYPES.MULTIPLA_ESCOLHA:
      return {
        ...baseAnswer,
        opcoesIds: (Array.isArray(answer.valor) ? answer.valor : [])
          .map((value) => findQuestionOptionByValue(question, value))
          .filter((opcao) => opcao?.id)
          .map((opcao) => opcao.id),
      };
    case FORMULARIO_GLOBAL_QUESTION_TYPES.ESCALA_ESTRELAS:
    case FORMULARIO_GLOBAL_QUESTION_TYPES.ESCALA_LIKERT:
    case FORMULARIO_GLOBAL_QUESTION_TYPES.RESPOSTA_UNICA:
    default: {
      const opcao = findQuestionOptionByValue(question, answer.valor);

      return {
        ...baseAnswer,
        opcaoId: opcao?.id || null,
      };
    }
  }
};

export const serializeFormularioGlobalAnswers = (sections, answers) =>
  sections.flatMap((section) =>
    section.perguntas
      .map((question) =>
        serializeQuestionAnswer(
          question,
          answers[question._formularioGlobalPreviewKey]
        )
      )
      .filter(
        (answer) =>
          hasFormularioGlobalValue(answer.valorTexto) ||
          hasFormularioGlobalValue(answer.valorNumero) ||
          hasFormularioGlobalValue(answer.valorBooleano) ||
          hasFormularioGlobalValue(answer.opcaoId) ||
          hasFormularioGlobalValue(answer.opcoesIds) ||
          hasFormularioGlobalValue(answer.selecoesMatriz) ||
          hasFormularioGlobalValue(answer.comentario)
      )
  );

const findQuestionOptionById = (question, id) =>
  hasFormularioGlobalValue(id)
    ? (question.opcoes || []).find((opcao) => String(opcao.id) === String(id))
    : null;

const getOptionValue = (opcao) => (opcao ? opcao.valor ?? opcao.id : null);

const deserializeQuestionAnswer = (question, persisted = {}) => {
  const comentario = persisted.comentario || '';

  switch (question.tipo) {
    case FORMULARIO_GLOBAL_QUESTION_TYPES.MATRIZ_SELECAO:
      return {
        valor: deserializeMatrixAnswer(question, persisted.selecoesMatriz),
        comentario,
      };
    case FORMULARIO_GLOBAL_QUESTION_TYPES.RESPOSTA_CURTA:
    case FORMULARIO_GLOBAL_QUESTION_TYPES.TEXTO_PARAGRAFO:
      return { valor: persisted.valorTexto ?? '', comentario };
    case FORMULARIO_GLOBAL_QUESTION_TYPES.NPS:
      return {
        valor: hasFormularioGlobalValue(persisted.valorNumero)
          ? persisted.valorNumero
          : null,
        comentario,
      };
    case FORMULARIO_GLOBAL_QUESTION_TYPES.SIMNAO:
      return {
        valor: hasFormularioGlobalValue(persisted.valorBooleano)
          ? persisted.valorBooleano
            ? '1'
            : '0'
          : null,
        comentario,
      };
    case FORMULARIO_GLOBAL_QUESTION_TYPES.MULTIPLA_ESCOLHA:
      return {
        valor: (persisted.opcoesIds || [])
          .map((id) => findQuestionOptionById(question, id))
          .filter(Boolean)
          .map(getOptionValue),
        comentario,
      };
    case FORMULARIO_GLOBAL_QUESTION_TYPES.ESCALA_ESTRELAS:
    case FORMULARIO_GLOBAL_QUESTION_TYPES.ESCALA_LIKERT:
    case FORMULARIO_GLOBAL_QUESTION_TYPES.RESPOSTA_UNICA:
    default:
      return {
        valor: getOptionValue(
          findQuestionOptionById(question, persisted.opcaoId)
        ),
        comentario,
      };
  }
};

export const deserializeFormularioGlobalAnswers = (
  sections,
  respostas = []
) => {
  const respostasPorPergunta = new Map(
    (respostas || [])
      .filter((resposta) => hasFormularioGlobalValue(resposta?.perguntaId))
      .map((resposta) => [String(resposta.perguntaId), resposta])
  );

  const answers = {};

  sections.forEach((section) => {
    section.perguntas.forEach((question) => {
      const persisted = respostasPorPergunta.get(String(question.id));

      if (!persisted) return;

      answers[question._formularioGlobalPreviewKey] = deserializeQuestionAnswer(
        question,
        persisted
      );
    });
  });

  return answers;
};

function QuestionImage({ item }) {
  const imageUrl = getImageUrl(item);

  if (!imageUrl) return null;

  return (
    <div className={classes.imageFrame}>
      <img
        alt={item.titulo || ''}
        src={imageUrl}
        style={{
          maxHeight: item.imagemAltura || undefined,
          width: item.imagemLargura || undefined,
        }}
      />
    </div>
  );
}

function ScaleOption({ checked, disabled, label, onChange }) {
  return (
    <button
      className={`${classes.scaleOption} ${
        checked ? classes.scaleOptionSelected : ''
      }`}
      disabled={disabled}
      onClick={onChange}
      type="button"
    >
      {label}
    </button>
  );
}

function StarRatingOption({ checked, disabled, label, onChange }) {
  return (
    <button
      className={`${classes.starButton} ${
        checked ? classes.starButtonSelected : ''
      }`}
      disabled={disabled}
      onClick={onChange}
      title={label}
      type="button"
    >
      <Star fill={checked ? 'currentColor' : 'none'} size={26} />
    </button>
  );
}

function QuestionInput({
  answer,
  disabled,
  error,
  onChange,
  question,
  questionKey,
}) {
  const intl = useIntl();
  const value = getQuestionValue(answer, question);
  const selectedValues = Array.isArray(value) ? value : [];

  switch (question.tipo) {
    case FORMULARIO_GLOBAL_QUESTION_TYPES.MATRIZ_SELECAO:
      return (
        <FormularioGlobalMatrixInput
          disabled={disabled}
          error={error}
          onChange={onChange}
          question={question}
          value={value}
        />
      );
    case FORMULARIO_GLOBAL_QUESTION_TYPES.RESPOSTA_CURTA:
      return (
        <Input
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          value={value || ''}
        />
      );
    case FORMULARIO_GLOBAL_QUESTION_TYPES.TEXTO_PARAGRAFO:
      return (
        <Input
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          rows={4}
          type="textarea"
          value={value || ''}
        />
      );
    case FORMULARIO_GLOBAL_QUESTION_TYPES.MULTIPLA_ESCOLHA:
      return (
        <div className={classes.options}>
          {question.opcoes.map((opcao, index) => {
            const optionValue = String(opcao.valor ?? opcao.id);
            const checked = selectedValues.includes(optionValue);

            return (
              <CustomInput
                checked={checked}
                disabled={disabled}
                id={`pergunta-${questionKey}-opcao-${opcao.ordem || index}`}
                key={optionValue}
                label={opcao.texto || opcao.titulo}
                onChange={(event) => {
                  const nextValues = event.target.checked
                    ? [...selectedValues, optionValue]
                    : selectedValues.filter((item) => item !== optionValue);

                  onChange(nextValues);
                }}
                type="checkbox"
              />
            );
          })}
        </div>
      );
    case FORMULARIO_GLOBAL_QUESTION_TYPES.ESCALA_ESTRELAS:
      return (
        <div className={classes.starRating}>
          {question.opcoes.map((opcao, index) => {
            const optionValue = String(opcao.valor ?? opcao.id);
            const selectedIndex = question.opcoes.findIndex(
              (item) => String(item.valor ?? item.id) === String(value)
            );

            return (
              <StarRatingOption
                checked={selectedIndex >= 0 && index <= selectedIndex}
                disabled={disabled}
                key={optionValue}
                label={opcao.texto || opcao.titulo || optionValue}
                onChange={() => onChange(optionValue)}
              />
            );
          })}
        </div>
      );
    case FORMULARIO_GLOBAL_QUESTION_TYPES.NPS:
      return (
        <div className={classes.inlineOptions}>
          {question.opcoes.map((opcao) => {
            const optionValue = String(opcao.valor ?? opcao.id);

            return (
              <ScaleOption
                checked={String(value) === optionValue}
                disabled={disabled}
                key={optionValue}
                label={opcao.texto || opcao.titulo || optionValue}
                onChange={() => onChange(optionValue)}
              />
            );
          })}
        </div>
      );
    case FORMULARIO_GLOBAL_QUESTION_TYPES.SIMNAO:
    case FORMULARIO_GLOBAL_QUESTION_TYPES.ESCALA_LIKERT:
    case FORMULARIO_GLOBAL_QUESTION_TYPES.RESPOSTA_UNICA:
    default:
      return (
        <div className={classes.options}>
          {question.opcoes.map((opcao, index) => {
            const optionValue = String(opcao.valor ?? opcao.id);

            return (
              <CustomInput
                checked={String(value) === optionValue}
                disabled={disabled}
                id={`pergunta-${questionKey}-opcao-${opcao.ordem || index}`}
                key={optionValue}
                label={opcao.texto || opcao.titulo}
                onChange={() => onChange(optionValue)}
                type="radio"
              />
            );
          })}
          {question.tipo === FORMULARIO_GLOBAL_QUESTION_TYPES.SIMNAO &&
            question.opcoes.length === 0 && (
              <>
                <CustomInput
                  checked={String(value) === '1'}
                  disabled={disabled}
                  id={`pergunta-${questionKey}-sim`}
                  label={intl.formatMessage({ id: 'labelSim' })}
                  onChange={() => onChange('1')}
                  type="radio"
                />
                <CustomInput
                  checked={String(value) === '0'}
                  disabled={disabled}
                  id={`pergunta-${questionKey}-nao`}
                  label={intl.formatMessage({ id: 'labelNao' })}
                  onChange={() => onChange('0')}
                  type="radio"
                />
              </>
            )}
        </div>
      );
  }
}

function Question({
  answer,
  className = '',
  disabled,
  error,
  onAnswerChange,
  question,
}) {
  const intl = useIntl();
  const questionKey = question._formularioGlobalPreviewKey;
  const comment = getQuestionComment(answer, question);
  const handleChange = (patch) => {
    if (disabled || !onAnswerChange) return;

    onAnswerChange(questionKey, {
      perguntaId: question.id,
      ...patch,
    });
  };

  return (
    <div className={`${classes.question} ${className}`}>
      <div className={classes.questionTitle}>
        {question.titulo}
        {question.obrigatoria && <span className={classes.required}>*</span>}
      </div>
      <QuestionImage item={question} />
      <QuestionInput
        answer={answer}
        disabled={disabled}
        error={error}
        onChange={(valor) => handleChange({ valor })}
        question={question}
        questionKey={questionKey}
      />
      {question.permitirComentario && (
        <div className="mt-3">
          <Label>
            {intl.formatMessage({ id: 'labelComentario' })}
            {question.comentarioObrigatorio && (
              <span className={classes.required}>*</span>
            )}
          </Label>
          <Input
            disabled={disabled}
            onChange={(event) =>
              handleChange({ comentario: event.target.value })
            }
            rows={3}
            type="textarea"
            value={comment}
          />
        </div>
      )}
      {error &&
        question.tipo !== FORMULARIO_GLOBAL_QUESTION_TYPES.MATRIZ_SELECAO && (
          <div className={classes.error}>{error}</div>
        )}
    </div>
  );
}

function FormularioGlobalRespostaPreview({
  answers = {},
  disabled = false,
  embedded = false,
  errors = {},
  finishedFooter,
  finishedMessage,
  footer,
  formulario = {},
  onAnswerChange,
  pesquisa = {},
  sections: sectionsOverride,
  showDescription = true,
  singleQuestionMode = false,
  titleFallback = '',
}) {
  const intl = useIntl();
  const sections = getFormularioGlobalBranchingSections(
    sectionsOverride || buildFormularioGlobalSections(formulario, intl),
    answers,
    formulario.randomizarPerguntas
  );
  const title = formulario.titulo || pesquisa.nome || titleFallback;
  const description = formulario.descricao || pesquisa.mensagemConvite;
  const pageClassName = `${classes.page} ${embedded ? classes.embedded : ''}`;
  const containerClassName = `${classes.container} ${
    singleQuestionMode ? classes.singleQuestionContainer : ''
  }`;
  const headerClassName = `${classes.header} ${
    singleQuestionMode ? classes.singleQuestionHeader : ''
  } ${
    singleQuestionMode && sections.length === 0
      ? classes.singleQuestionHeaderStandalone
      : ''
  }`;
  const titleClassName = `${classes.title} ${
    singleQuestionMode ? classes.singleQuestionTitle : ''
  }`;
  const descriptionClassName = `${classes.description} ${
    singleQuestionMode ? classes.singleQuestionDescription : ''
  }`;
  const actionsClassName = `${classes.actions} ${
    singleQuestionMode ? classes.singleQuestionActions : ''
  }`;

  if (finishedMessage) {
    return (
      <div className={pageClassName}>
        <div className={containerClassName}>
          <div className={classes.finished}>
            <div>{finishedMessage}</div>
            {finishedFooter && (
              <div className={classes.finishedActions}>{finishedFooter}</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={pageClassName}>
      <div className={containerClassName}>
        <div className={headerClassName}>
          {title && <h1 className={titleClassName}>{title}</h1>}
          {showDescription && description && (
            <p className={descriptionClassName}>{description}</p>
          )}
        </div>

        {sections.map((section) => (
          <div
            className={`${classes.section} ${
              singleQuestionMode ? classes.singleQuestionSection : ''
            }`}
            key={
              section._formularioGlobalPreviewKey || section.id || section.ordem
            }
          >
            {section.titulo && (
              <h2
                className={`${classes.sectionTitle} ${
                  singleQuestionMode ? classes.singleQuestionSectionTitle : ''
                }`}
              >
                {section.titulo}
              </h2>
            )}
            <QuestionImage item={section} />
            {section.descricao && (
              <p
                className={`${classes.sectionDescription} ${
                  singleQuestionMode
                    ? classes.singleQuestionSectionDescription
                    : ''
                }`}
              >
                {section.descricao}
              </p>
            )}
            {section.perguntas.map((question) => (
              <Question
                answer={answers}
                className={
                  singleQuestionMode ? classes.singleQuestionQuestion : ''
                }
                disabled={disabled}
                error={errors[question._formularioGlobalPreviewKey]}
                key={question._formularioGlobalPreviewKey}
                onAnswerChange={onAnswerChange}
                question={question}
              />
            ))}
          </div>
        ))}

        {footer && <div className={actionsClassName}>{footer}</div>}
      </div>
    </div>
  );
}

export default FormularioGlobalRespostaPreview;
